import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Pressable, TextInput } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useIsTenantAdmin } from "@visioni/a3s-access/app";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { listSitesQuery, updateSiteMutation } from "@visioni/a3s-provisioning/app";
import { SiteId } from "@visioni/a3s-schema";
import { showErrorToast } from "@src/utils/toast";

interface EditableSiteNameProps {
  readonly siteId: string;
  readonly currentName: string;
}

export const EditableSiteName = memo(function EditableSiteName({ siteId, currentName }: EditableSiteNameProps) {
  const { theme } = useUnistyles();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<TextInput>(null);
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useIsTenantAdmin();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const { queryKey: sitesQueryKey } = listSitesQuery(apiConfig, {});

  const { mutate: updateSite, isPending } = useMutation({
    mutationFn: (body: { siteName: string }) => {
      const { mutationFn } = updateSiteMutation(mutationConfig, { siteId: SiteId.fromString(siteId) });
      return mutationFn(body);
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: sitesQueryKey });
      const previous = queryClient.getQueryData<{ sites: Array<{ siteId: string; siteName: string }> }>(sitesQueryKey);
      queryClient.setQueryData<{ sites: Array<{ siteId: string; siteName: string }> }>(sitesQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          sites: old.sites.map((s) => (String(s.siteId) === siteId ? { ...s, siteName: body.siteName } : s)),
        };
      });
      setIsEditing(false);
      return { previous };
    },
    onError: (error, _body, context) => {
      if (context?.previous) {
        queryClient.setQueryData(sitesQueryKey, context.previous);
      }
      showErrorToast({ error, description: "Failed to update site name" });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sitesQueryKey });
    },
  });

  const handleEditPress = useCallback(() => {
    setDraft(currentName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [currentName]);

  const handleDiscard = useCallback(() => {
    setIsEditing(false);
    setDraft("");
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed.length === 0 || trimmed === currentName) {
      setIsEditing(false);
      return;
    }
    updateSite({ siteName: trimmed });
  }, [draft, currentName, updateSite]);

  if (isEditing) {
    return (
      <View style={styles.editRow}>
        <View style={styles.inputWrapper}>
          <TextInput
            ref={inputRef}
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={handleSave}
            placeholder="Site name"
            placeholderTextColor={theme.colors.textMuted}
            style={styles.input}
            autoFocus
            editable={!isPending}
            maxLength={64}
          />
        </View>
        <Pressable
          onPress={handleSave}
          disabled={isPending}
          style={[styles.actionButton, { backgroundColor: theme.colors.interactive.buttons.default_background }]}
          accessibilityRole="button"
          accessibilityLabel="Save site name"
        >
          <Icon name="tick" size={18} color={theme.colors.white.default} />
        </Pressable>
        <Pressable
          onPress={handleDiscard}
          disabled={isPending}
          style={[styles.actionButton, { backgroundColor: theme.colors.surfaceCard }]}
          accessibilityRole="button"
          accessibilityLabel="Discard changes"
        >
          <Icon name="close" size={18} color={theme.colors.textMuted} />
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.displayRow}>
      <Text textStyle="lg/semibold" color="text">
        {currentName}
      </Text>
      {isTenantAdmin && (
        <Pressable
          onPress={handleEditPress}
          style={styles.editButton}
          accessibilityRole="button"
          accessibilityLabel="Rename site"
        >
          <Icon name="edit" size={18} color={theme.colors.textMuted} />
        </Pressable>
      )}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  displayRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  editButton: {
    padding: theme.spacing.sm,
    borderRadius: 4,
  },
  editRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
  inputWrapper: {
    flex: 1,
    maxWidth: 320,
  },
  input: {
    height: 32,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: theme.spacing["2xl"],
    fontSize: 14,
    fontWeight: "400",
    outlineStyle: "none",
    color: theme.colors.text,
    borderColor: theme.colors.border,
  } as any,
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
}));
